import React, { useContext, useMemo, useState } from "react";
import axios from "axios";
import { FaChevronLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  JOURNEY_MODAL_FLAG,
  SOCIAL_SIGNUP_DETAILS,
} from "../constants/storageKeys";
import { UserContext } from "../contexts/UserContext";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

const gradeOptions = [
  { label: "6", value: "6th Grade" },
  { label: "7", value: "7th Grade" },
  { label: "8", value: "8th Grade" },
  { label: "9", value: "9th Grade" },
  { label: "10", value: "10th Grade" },
];

const boardOptions = [
  {
    id: "cbse",
    label: "CBSE",
    description: "Central Board of Secondary Education",
  },
  {
    id: "icse",
    label: "ICSE",
    description: "Indian Certificate of Secondary Education",
  },
  {
    id: "igcse",
    label: "IGCSE",
    description: "IGCSE",
  },
  {
    id: "ib",
    label: "IB",
    description: "International Baccalaureate",
  },
];

const getBoardLabelFromId = (boardId) => {
  const match = boardOptions.find((board) => board.id === boardId);
  return match ? match.label : boardId;
};

const getPrefilledOnboardingName = () => {
  if (typeof window === "undefined") return "";
  const storageSources = [
    () => localStorage.getItem("onboardingInfo"),
    () => localStorage.getItem(SOCIAL_SIGNUP_DETAILS),
    () => localStorage.getItem("userToken"),
  ];

  for (const getSource of storageSources) {
    try {
      const rawValue = getSource();
      if (!rawValue) continue;
      const parsed = JSON.parse(rawValue);
      const candidate = parsed?.name || parsed?.fullName;
      if (candidate) {
        return candidate;
      }
    } catch (error) {
      console.error("Unable to prefill onboarding name:", error);
    }
  }
  return "";
};

const LeftPanel = () => (
  <div className="flex flex-col items-center justify-center bg-gray-50 w-full md:w-5/12 p-8 gap-6 md:py-12">
    <div className="rounded-[24px] overflow-hidden max-w-xs w-full border border-white/10">
      <img
        src="/images/tutor_square.png"
        alt="Aanya"
        className=" w-full h-full object-center rounded-2xl"
      />
    </div>
    <div className="text-center space-y-1">
      <p className="text-lg font-semibold">Nice to meet you! I'm Aanya.</p>
      <p className="text-sm">
        I&apos;ll guide you through a quick setup. It only takes a minute.
      </p>
    </div>
  </div>
);

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState(() => getPrefilledOnboardingName());
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const navigate = useNavigate();
  const { updateProfileDetails, fetchUserProfile } = useContext(UserContext);

  const progress = useMemo(() => (step === 1 ? 33 : 66), [step]);

  const resetOnboardingInfo = () => {
    if (typeof window === "undefined") return;
    try {
      localStorage.removeItem("onboardingInfo");
    } catch (error) {
      console.error("Unable to reset onboarding info:", error);
    }
  };

  const handleNext = () => {
    if (!name.trim()) return;
    setStep(2);
  };

  const handleBackToLogin = async () => {
    const token = localStorage.getItem("token");

    // if (token) {
    //   try {
    //     await axios.post(
    //       `${baseUrl}/v2/auth/signout`,
    //       {},
    //       {
    //         headers: {
    //           Authorization: `Bearer ${token}`,
    //         },
    //       }
    //     );
    //   } catch (error) {
    //     console.error("Silent logout failed:", error);
    //   }
    // }

    localStorage.removeItem("token");
    localStorage.removeItem("userToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("profileUpdated");
    localStorage.removeItem("onboardingComplete");
    localStorage.removeItem("onboardingInfo");
    localStorage.removeItem(SOCIAL_SIGNUP_DETAILS);

    // Clear all remaining localStorage items
    localStorage.clear();

    // Clear all sessionStorage items
    sessionStorage.clear();

    navigate("/login", { replace: true });
  };

  const handleStartLearning = async () => {
    if (!selectedGrade || !selectedBoard || !name.trim()) return;
    if (isSavingPreferences) return;

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      navigate("/login", { replace: true });
      return;
    }

    const trimmedName = name.trim();
    const boardLabel = getBoardLabelFromId(selectedBoard);
    const onboardingPayload = {
      name: trimmedName,
      grade: String(selectedGrade),
      board: boardLabel,
      completedAt: new Date().toISOString(),
    };

    const profilePayload = {
      fullName: trimmedName,
      grade: String(selectedGrade),
      board: boardLabel,
    };

    try {
      setIsSavingPreferences(true);
      const response = await axios.put(
        `${baseUrl}/v2/profile/update-profile`,
        profilePayload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const serverProfile = response?.data || {};
      const resolvedName = serverProfile.fullName?.trim() || trimmedName;
      const resolvedBoard = serverProfile.board || boardLabel;
      const resolvedGrade = serverProfile.grade || String(selectedGrade);

      updateProfileDetails({
        name: resolvedName,
        board: resolvedBoard,
        grade: String(resolvedGrade),
      });

      localStorage.setItem("profileUpdated", "true");
      localStorage.setItem("onboardingInfo", JSON.stringify(onboardingPayload));
      localStorage.setItem("onboardingComplete", "true");
      localStorage.setItem(JOURNEY_MODAL_FLAG, "true");
      toast.success("Profile preferences saved!", {
        position: "top-center",
        autoClose: 2500,
        theme: "colored",
      });
      if (typeof fetchUserProfile === "function") {
        try {
          await fetchUserProfile();
        } catch (refreshError) {
          console.error(
            "Failed to refresh profile after onboarding:",
            refreshError
          );
        }
      }
      navigate("/");
      resetOnboardingInfo();
    } catch (error) {
      console.error(
        "Error saving onboarding preferences:",
        error.response?.data || error.message
      );
      toast.error(
        error.response?.data?.detail ||
          "We couldn't save your preferences. Please try again."
      );
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const isStartButtonDisabled =
    !selectedGrade || !selectedBoard || isSavingPreferences;

  return (
    <div className="min-h-screen py-10 px-4 flex items-center justify-center bg-primary">
      <div className="w-full max-w-6xl space-y-4 bg-white rounded-[32px] overflow-hidden">
        <div className="flex items-center">
          <button
            type="button"
            onClick={handleBackToLogin}
            className="p-10 text-primary text-xl hover:text-primary-dark flex items-center gap-2"
          >
            <FaChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span>Back</span>
          </button>
        </div>
        <div className="mx-auto flex flex-col md:flex-row rounded-[32px] bg-white shadow-2xl overflow-hidden">
          <LeftPanel />
          <div className="flex-1 px-6 py-8 md:p-12 space-y-8">
            {step === 1 ? (
              <div className="h-full flex flex-col">
                <div>
                  <p className="text-sm font-semibold text-slate-500">
                    {progress}% Complete
                  </p>
                  <div className="w-full h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-8">
                    What should I call you?
                  </h1>
                  <p className="text-slate-500 mt-2">
                    Give us a name so we know how to personalize your
                    experience.
                  </p>
                </div>
                <div className="flex-1 flex flex-col justify-between mt-10">
                  <div>
                    <label
                      htmlFor="onboarding-name"
                      className="text-sm uppercase tracking-widest text-slate-400"
                    >
                      Enter your name
                    </label>
                    <input
                      id="onboarding-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-4 w-full border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-transparent focus:outline-none text-xl text-slate-900 placeholder:text-slate-400"
                      placeholder="Type here"
                    />
                  </div>
                  <div className="mt-10 flex justify-end">
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={!name.trim()}
                      className={`px-8 py-3 rounded-full text-white font-semibold flex items-center gap-2 transition focus:outline-none shadow-lg ${
                        name.trim()
                          ? "bg-primary hover:bg-primary-dark"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Next
                      <span aria-hidden="true" className="text-xl">
                        →
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-10">
                <div>
                  <p className="text-sm font-semibold text-slate-500">
                    {progress}% Complete
                  </p>
                  <div className="w-full h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-8">
                    Let&apos;s customize your profile
                  </h1>
                  <p className="text-slate-500 mt-2">
                    Choose your grade and board so we can show the most relevant
                    lessons.
                  </p>
                </div>
                <div className="space-y-5">
                  <div>
                    <p className="uppercase text-xs font-semibold text-slate-400 mb-4">
                      Select Grade
                    </p>
                    <div className="flex flex-wrap gap-4">
                      {gradeOptions.map((grade) => (
                        <button
                          type="button"
                          key={grade.value}
                          onClick={() => setSelectedGrade(grade.value)}
                          className={`h-16 w-16 rounded-full border-2 text-xl font-semibold transition ${
                            selectedGrade === grade.value
                              ? "border-primary bg-primary text-white shadow-md"
                              : "border-gray-200 text-slate-600 hover:border-primary/60"
                          }`}
                        >
                          {grade.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="uppercase text-xs font-semibold text-slate-400 mb-4">
                      Select Board
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {boardOptions.map((board) => (
                        <button
                          type="button"
                          key={board.id}
                          onClick={() => setSelectedBoard(board.id)}
                          className={`text-left p-4 rounded-2xl border transition h-full ${
                            selectedBoard === board.id
                              ? "border-primary bg-primary shadow-md text-white"
                              : "border-gray-200 hover:border-primary/60"
                          }`}
                        >
                          <p
                            className={`text-lg font-semibold text-slate-900 ${
                              selectedBoard === board.id
                                ? " text-white"
                                : " text-slate-900"
                            }`}
                          >
                            {board.label}
                          </p>
                          <p
                            className={`text-sm text-slate-500 mt-1 ${
                              selectedBoard === board.id
                                ? " text-white"
                                : " text-slate-900"
                            }`}
                          >
                            {board.description}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-slate-500 font-semibold hover:text-slate-700"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    disabled={isStartButtonDisabled}
                    onClick={handleStartLearning}
                    className={`px-8 py-3 rounded-full text-white font-semibold shadow-lg transition focus:outline-none ${
                      !isStartButtonDisabled
                        ? "bg-primary hover:bg-primary-dark"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {isSavingPreferences ? "Saving..." : "Start Learning"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
